import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';

import { BehaviorSubject, Observable, catchError, finalize, first, of, switchMap, tap, throwError } from 'rxjs';

import { AuthServiceError, AuthToken } from '@personal-area/global/authorization/models';
import { AuthService } from '@personal-area/global/authorization/services';
import { IS_PRIVATE_API } from '@personal-area/global/http/models';
import { RedirectBackService } from '@personal-area/global/navigation';

const addToken = (req: HttpRequest<any>, token: AuthToken): HttpRequest<any> =>
  req.clone({
    setHeaders: {
      Authorization: 'Bearer ' + token.accessToken,
    },
  });

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private _isRefreshingToken = false;
  private _tokenSubject = new BehaviorSubject<AuthToken | null>(null);
  private _authService = inject(AuthService);
  private _redirectBackService = inject(RedirectBackService);
  private _platformId = inject(PLATFORM_ID);

  public intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!req.context.get(IS_PRIVATE_API)) {
      return next.handle(req);
    }

    if (!this._authService.authToken || this._authService.isRefreshTokenExpired) {
      return this._logout();
    }

    return next.handle(addToken(req, this._authService.authToken))
      .pipe(catchError((err: HttpErrorResponse) => this._handleError(err, req, next)));
  }

  private _logout(): Observable<never> {
    if (isPlatformServer(this._platformId)) {
      this._authService.logout();
    } else {
      this._authService.logout(false);
      this._redirectBackService.redirectLogin();
    }

    return throwError(() => new HttpErrorResponse({
      status: 401,
      error: 'logout',
    }));
  }

  private _handleError(
    err: HttpErrorResponse,
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<any> {
    const exception = err.error
      ? err.error.exception || err.error.mapException
      : null;

    if (!exception) {
      return throwError(
        () =>
          new HttpErrorResponse({
            error: {
              exception: {
                code: 0,
                token: 'no-token',
              },
            },
            headers: err.headers,
            status: err.status,
            statusText: err.statusText,
            url: err.url,
          }),
      );
    }

    const isTokenValid = ![
      AuthServiceError.GRPCUnauthenticatedException,
      AuthServiceError.TokenExpiredException,
    ].includes(exception.code);
    const isPartnerAuthenticated = !(
      req.url.includes('/partners/') && err.status === 401
    );

    if (isTokenValid && isPartnerAuthenticated) {
      return throwError(() => err);
    }

    return this._getTokenForResponse().pipe(
      switchMap((token) => next.handle(addToken(req, token))),
    );
  }

  private _getTokenForResponse(): Observable<AuthToken> {
    if (this._isRefreshingToken) {
      return this._tokenSubject.pipe(first(Boolean));
    }

    // reset here so that the following requests wait
    // until the token comes back from the refreshToken call
    this._tokenSubject.next(null);
    this._isRefreshingToken = true;

    if (!this._authService.authToken?.refreshToken) {
      return this._logout();
    }

    return this._authService.refreshAuthToken().pipe(
      tap(token => this._tokenSubject.next(token)),
      catchError(() => this._logout()),
      finalize(() => (this._isRefreshingToken = false)),
    );
  }
}
